/**
 * GoodGuardian — Court Orders Screen
 * List active/modified/expired orders, add new orders, view detail
 */
import { useState, useRef } from 'react'
import AppHeader from '@/components/layout/AppHeader'
import Button from '@/components/common/Button'
import Input from '@/components/common/Input'
import { useApp } from '@/context/AppContext'
import * as apiModule from '@/utils/auth'

const STATUS_CONFIG = {
  active:   { label: 'Active',    bg: 'bg-green-100',  text: 'text-green-700',  dot: 'bg-green-500'  },
  modified: { label: 'Modified',  bg: 'bg-amber-100',  text: 'text-amber-700',  dot: 'bg-amber-500'  },
  expired:  { label: 'Expired',   bg: 'bg-slate-100',  text: 'text-slate-500',  dot: 'bg-slate-400'  },
}

const EMPTY_FORM = {
  title: '',
  jurisdiction: '',
  date_issued: '',
  key_terms: '',
  status: 'active',
}

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.active
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.bg} ${cfg.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  )
}

function AddOrderModal({ onClose, onSave }) {
  const [form, setForm]       = useState(EMPTY_FORM)
  const [saving, setSaving]   = useState(false)
  const [scanning, setScanning] = useState(false)
  const [scanMsg, setScanMsg] = useState('')
  const [formErrors, setFormErrors] = useState({})
  const fileRef   = useRef(null)
  const cameraRef = useRef(null)

  const set = (k, v) => { setForm(p => ({ ...p, [k]: v })); setFormErrors(prev => ({ ...prev, [k]: '' })) }

  const handleSave = async () => {
    const errors = {}
    if (!form.title.trim()) errors.title = 'Order Title is required'
    if (!form.jurisdiction.trim()) errors.jurisdiction = 'Jurisdiction is required'
    if (!form.date_issued) errors.date_issued = 'Date Issued is required'
    if (Object.keys(errors).length > 0) { setFormErrors(errors); return }
    setFormErrors({})
    setSaving(true)
    await onSave(form)
    setSaving(false)
    onClose()
  }

  const handleScan = async (file) => {
    if (!file) return
    setScanning(true)
    setScanMsg('')
    try {
      const result = await apiModule.data.scanOrder(file)
      setForm(prev => ({
        title:        result.title        || prev.title,
        jurisdiction: result.jurisdiction || prev.jurisdiction,
        date_issued:  result.date_issued  || prev.date_issued,
        status:       result.status       || prev.status,
        key_terms:    result.key_terms    || prev.key_terms,
      }))
      const pct = Math.round((result.confidence || 0) * 100)
      setScanMsg(`✓ Document scanned (${pct}% confidence). Review the fields below and edit as needed before saving.`)
    } catch (err) {
      if (err.error === 'scanned_pdf') {
        setScanMsg('📷 This PDF is scanned (image-only). Tap Camera to photograph the document directly for OCR instead.')
      } else {
        setScanMsg(`⚠️ Scan failed: ${err.message || 'Unknown error. Please try again.'}`)
      }
    } finally {
      setScanning(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end">
      <div className="bg-white w-full rounded-t-2xl p-5 max-h-[90vh] overflow-y-auto">

        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-slate-800">Add Court Order</h2>
          <button onClick={onClose} className="tap-target text-slate-500" aria-label="Close add court order dialog">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Scan buttons */}
        <div className="flex gap-2 mb-3">
          <button
            onClick={() => cameraRef.current?.click()}
            disabled={scanning}
            className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-blue-200 bg-blue-50 text-blue-700 text-sm font-semibold active:bg-blue-100 disabled:opacity-50 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            📷 Camera
          </button>
          <button
            onClick={() => fileRef.current?.click()}
            disabled={scanning}
            className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-slate-200 bg-slate-50 text-slate-700 text-sm font-semibold active:bg-slate-100 disabled:opacity-50 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
            📄 Upload File
          </button>
        </div>

        {/* Hidden file inputs */}
        <input
          ref={cameraRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={e => { handleScan(e.target.files?.[0]); e.target.value = '' }}
        />
        <input
          ref={fileRef}
          type="file"
          accept="application/pdf,image/*"
          className="hidden"
          onChange={e => { handleScan(e.target.files?.[0]); e.target.value = '' }}
        />

        {/* Scanning state */}
        {scanning && (
          <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 mb-3">
            <svg className="w-5 h-5 text-blue-500 animate-spin flex-shrink-0" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
            </svg>
            <p className="text-sm text-blue-700 font-medium">Scanning document with AI…</p>
          </div>
        )}

        {/* Scan result message */}
        {scanMsg && !scanning && (
          <div className={`text-xs rounded-xl px-4 py-3 mb-3 leading-relaxed ${
            scanMsg.startsWith('✓')
              ? 'bg-green-50 text-green-700 border border-green-200'
              : 'bg-amber-50 text-amber-700 border border-amber-200'
          }`}>
            {scanMsg}
          </div>
        )}

        {/* Divider */}
        <div className="flex items-center gap-3 mb-4">
          <div className="flex-1 h-px bg-slate-200" />
          <span className="text-xs text-slate-500 font-medium">or fill in manually</span>
          <div className="flex-1 h-px bg-slate-200" />
        </div>

        {/* Form fields */}
        <div className="space-y-3">
          <Input
            label="Order Title *"
            placeholder="e.g. Final Custody Order, Visitation Schedule"
            value={form.title}
            onChange={e => set('title', e.target.value)}
          />
          {formErrors.title && <p className="text-red-500 text-sm -mt-2">{formErrors.title}</p>}
          <Input
            label="Jurisdiction / Court *"
            placeholder="e.g. Los Angeles Superior Court"
            value={form.jurisdiction}
            onChange={e => set('jurisdiction', e.target.value)}
          />
          {formErrors.jurisdiction && <p className="text-red-500 text-sm -mt-2">{formErrors.jurisdiction}</p>}
          <Input
            label="Date Issued *"
            type="date"
            value={form.date_issued}
            onChange={e => set('date_issued', e.target.value)}
          />
          {formErrors.date_issued && <p className="text-red-500 text-sm -mt-2">{formErrors.date_issued}</p>}

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
            <div className="flex gap-2">
              {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                <button
                  key={key}
                  onClick={() => set('status', key)}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium border-2 transition-all ${
                    form.status === key
                      ? `${cfg.bg} ${cfg.text} border-current`
                      : 'bg-white text-slate-700 border-slate-300'
                  }`}
                >
                  {cfg.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Key Terms / Summary</label>
            <textarea
              className="w-full border border-slate-300 rounded-xl px-3 py-2 text-sm text-slate-800 min-h-[100px] resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Summarize the key terms, custody schedule, or compliance requirements..."
              value={form.key_terms}
              onChange={e => set('key_terms', e.target.value)}
            />
          </div>
        </div>

        <div className="mt-5">
          <Button onClick={handleSave} disabled={saving || scanning} className="w-full">
            {saving ? 'Saving…' : 'Save Order'}
          </Button>
        </div>
      </div>
    </div>
  )
}

function OrderDetail({ order, onBack, onUpdateStatus, onDelete, canEdit }) {
  const [showStatusMenu, setShowStatusMenu] = useState(false)
  const cfg = STATUS_CONFIG[order.status] || STATUS_CONFIG.active

  return (
    <div className="flex flex-col h-full bg-white">
      <AppHeader title="Order Detail" showBack onBack={onBack} />
      <div className="screen no-scrollbar px-4 py-5 space-y-4">

        {/* Title + status */}
        <div className="card p-4">
          <div className="flex items-start justify-between gap-3 mb-2">
            <div className="flex-1">
              <h2 className="text-lg font-bold text-slate-800 leading-snug">{order.title}</h2>
              <p className="text-sm text-slate-500 mt-0.5">{order.jurisdiction}</p>
            </div>
            <StatusBadge status={order.status} />
          </div>
          <p className="text-xs text-slate-500">
            Issued: {order.date_issued ? new Date(order.date_issued).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '—'}
          </p>
          {order.ownership === 'partner' && (
            <p className="text-xs text-blue-600 mt-1 font-medium">📋 Added by co-parent</p>
          )}
        </div>

        {/* Key terms */}
        {order.key_terms && (
          <div className="card p-4">
            <h3 className="text-sm font-semibold text-slate-700 mb-2">Key Terms & Summary</h3>
            <p className="text-sm text-slate-600 whitespace-pre-wrap leading-relaxed">{order.key_terms}</p>
          </div>
        )}

        {/* Compliance reminder */}
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
          <div className="flex gap-2">
            <span className="text-amber-500 text-lg">⚠️</span>
            <div>
              <p className="text-sm font-semibold text-amber-800">Compliance Notice</p>
              <p className="text-xs text-amber-700 mt-0.5">
                This order is stored for reference only. Always consult your attorney for legal interpretation. Non-compliance with court orders may result in contempt proceedings.
              </p>
            </div>
          </div>
        </div>

        {/* Actions — only for own orders */}
        {canEdit && (
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-slate-700">Update Status</h3>
            <div className="flex gap-2">
              {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                <button
                  key={key}
                  onClick={() => { onUpdateStatus(order.id, key); setShowStatusMenu(false) }}
                  className={`flex-1 py-2 rounded-xl text-xs font-medium border-2 transition-all ${
                    order.status === key
                      ? `${cfg.bg} ${cfg.text} border-current`
                      : 'bg-white text-slate-700 border-slate-300'
                  }`}
                >
                  {cfg.label}
                </button>
              ))}
            </div>

            <button
              onClick={() => onDelete(order.id)}
              className="w-full mt-2 py-3 rounded-xl text-sm font-medium text-red-600 bg-red-50 border border-red-200 hover:bg-red-100 transition-colors"
            >
              Delete Order
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

function OrderCard({ order, onTap }) {
  const cfg = STATUS_CONFIG[order.status] || STATUS_CONFIG.active
  return (
    <button
      onClick={onTap}
      className="w-full text-left card p-4 flex items-center gap-3 active:scale-[0.98] transition-transform"
    >
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${cfg.bg}`}>
        <svg className={`w-5 h-5 ${cfg.text}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-800 truncate">{order.title}</p>
        <p className="text-xs text-slate-500 truncate">{order.jurisdiction}</p>
        {order.date_issued && (
          <p className="text-xs text-slate-500">
            Issued {new Date(order.date_issued).toLocaleDateString()}
          </p>
        )}
      </div>
      <div className="flex flex-col items-end gap-1">
        <StatusBadge status={order.status} />
        {order.ownership === 'partner' && (
          <span className="text-xs text-blue-500">Co-parent</span>
        )}
      </div>
    </button>
  )
}

export default function Orders() {
  const { orders, addOrder, updateOrder, deleteOrder, coParent } = useApp()
  const [showAdd, setShowAdd] = useState(false)
  const [selectedId, setSelectedId] = useState(null)
  const [filter, setFilter] = useState('all')

  const selectedOrder = orders.find(o => o.id === selectedId)

  const filtered = filter === 'all'
    ? orders
    : orders.filter(o => o.status === filter)

  const ownOrders     = filtered.filter(o => o.ownership !== 'partner')
  const partnerOrders = filtered.filter(o => o.ownership === 'partner')

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this court order?')) return
    await deleteOrder(id)
    setSelectedId(null)
  }

  // Detail view
  if (selectedOrder) {
    return (
      <OrderDetail
        order={selectedOrder}
        onBack={() => setSelectedId(null)}
        onUpdateStatus={(id, status) => updateOrder(id, { status })}
        onDelete={handleDelete}
        canEdit={selectedOrder.ownership !== 'partner'}
      />
    )
  }

  return (
    <div className="flex flex-col h-full bg-white">
      <AppHeader title="Court Orders" />
      <div className="screen no-scrollbar px-4 py-5">

        {/* Co-parent sync banner */}
        {coParent && (
          <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-2xl px-3 py-2.5 mb-4">
            <span className="text-blue-500 text-base">🔗</span>
            <p className="text-xs text-blue-700 font-medium">
              Synced with {coParent.partner_name || 'co-parent'} — you can see each other's orders
            </p>
          </div>
        )}

        {/* Filter tabs */}
        <div className="flex gap-2 mb-4 overflow-x-auto no-scrollbar">
          {[['all','All'],['active','Active'],['modified','Modified'],['expired','Expired']].map(([key, label]) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap flex-shrink-0 transition-colors ${
                filter === key
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-100 text-slate-600'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Empty state */}
        {orders.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <p className="text-slate-700 font-semibold">No court orders yet</p>
            <p className="text-slate-500 text-sm mt-1">Add your custody orders and parenting plan to track compliance</p>
            <Button className="mt-5" onClick={() => setShowAdd(true)}>+ Add First Order</Button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Own orders */}
            {ownOrders.length > 0 && (
              <div className="space-y-2">
                {partnerOrders.length > 0 && (
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide px-1">Your Orders</p>
                )}
                {ownOrders.map(order => (
                  <OrderCard key={order.id} order={order} onTap={() => setSelectedId(order.id)} />
                ))}
              </div>
            )}

            {/* Partner orders */}
            {partnerOrders.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide px-1">Co-Parent's Orders</p>
                {partnerOrders.map(order => (
                  <OrderCard key={order.id} order={order} onTap={() => setSelectedId(order.id)} />
                ))}
              </div>
            )}

            {filtered.length === 0 && (
              <p className="text-center text-slate-500 text-sm py-8">No {filter} orders</p>
            )}
          </div>
        )}

        {/* Add button */}
        {orders.length > 0 && (
          <div className="mt-5">
            <Button onClick={() => setShowAdd(true)} className="w-full">+ Add Court Order</Button>
          </div>
        )}
      </div>

      {showAdd && (
        <AddOrderModal
          onClose={() => setShowAdd(false)}
          onSave={addOrder}
        />
      )}
    </div>
  )
}
